import { formatTime } from './format-time';
import type { AudioBlocks } from '@/api/types/backend-dto-types';
import { TimeRangeWithId } from '@/types';

type AudioBlock = AudioBlocks[number];
type AudioWord = AudioBlock['words'][number];

export const LOW_TRANSCRIPTION_CONFIDENCE_THRESHOLD = 0.65;

const getWordStartSeconds = (word: AudioWord, block: AudioBlock) => {
  const offsetSeconds = word.offsetMilliseconds / 1000;
  return offsetSeconds >= block.startTimeSeconds ? offsetSeconds : block.startTimeSeconds + offsetSeconds;
};

const getSpeakerIdentity = (block: AudioBlock) => {
  const { id, diarizationLabel, speaker } = block.audioFileSpeaker;

  return {
    key: speaker?.id ?? id,
    name: speaker?.name ?? `Speaker ${diarizationLabel}`,
  };
};

export interface TranscriptRow {
  blockId: string;
  startTimeSeconds: number;
  endTimeSeconds: number;
  timeLabel: string;
  speakerLabel: string | null;
  showsSpeakerGap: boolean;
  hasLowConfidence: boolean;
  text: string;
  wordTexts: string[];
  wordStartSeconds: number[];
}

/**
 * Flattens the speaker grouping into a positional row list. `showsSpeakerGap` and
 * `speakerLabel` carry what the nested speaker sections used to express.
 */
export const buildTranscriptRows = (blocks: AudioBlocks): TranscriptRow[] =>
  blocks.map((block, index) => {
    const speaker = getSpeakerIdentity(block);
    const previousBlock = blocks[index - 1];
    const isFirstOfSpeakerRun = (previousBlock ? getSpeakerIdentity(previousBlock).key : null) !== speaker.key;

    // A block with no words colours whole; one word spanning it reproduces that with
    // no special case downstream.
    const wordTexts = block.words.length ? block.words.map((word) => word.text) : [block.text];
    const wordStartSeconds = block.words.length
      ? block.words.map((word) => getWordStartSeconds(word, block))
      : [block.startTimeSeconds];

    return {
      blockId: block.id,
      startTimeSeconds: block.startTimeSeconds,
      endTimeSeconds: block.startTimeSeconds + block.durationSeconds,
      timeLabel: formatTime(block.startTimeSeconds),
      speakerLabel: isFirstOfSpeakerRun ? `${speaker.name}` : null,
      showsSpeakerGap: isFirstOfSpeakerRun && index > 0,
      hasLowConfidence: block.confidence !== null && block.confidence < LOW_TRANSCRIPTION_CONFIDENCE_THRESHOLD,
      text: wordTexts.join(' '),
      wordTexts,
      wordStartSeconds,
    };
  });

/**
 * The row the playhead is inside, or -1 during the silence between blocks. Binary
 * search over start-sorted rows.
 */
export const findActiveRowIndex = (rows: TranscriptRow[], seconds: number) => {
  let low = 0;
  let high = rows.length - 1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    const row = rows[mid];

    if (seconds < row.startTimeSeconds) high = mid - 1;
    else if (seconds >= row.endTimeSeconds) low = mid + 1;
    else return mid;
  }

  return -1;
};

// How many entries start strictly before `seconds` for an upper-bound binary search.
const countStartedBefore = (length: number, startAt: (index: number) => number, seconds: number) => {
  let low = 0;
  let high = length;

  while (low < high) {
    const mid = (low + high) >> 1;
    if (seconds > startAt(mid)) low = mid + 1;
    else high = mid;
  }

  return low;
};

// How many of the row's words the playhead has passed.
export const findPlayedWordCount = (row: TranscriptRow, seconds: number) =>
  countStartedBefore(row.wordStartSeconds.length, (index) => row.wordStartSeconds[index], seconds);

/**
 * How many rows the playhead has passed, which is what decides the played/unplayed phase of
 * every non-active row. Derived from row starts rather than from the active index so the
 * colouring is unchanged during the silence between blocks, where there is no active row.
 */
export const findPlayedRowCount = (rows: TranscriptRow[], seconds: number) =>
  countStartedBefore(rows.length, (index) => rows[index].startTimeSeconds, seconds);

// The row the playhead is inside, or the closest one when it lands in silence/before or after the recording
export const findNearestRowIndex = (rows: TranscriptRow[], seconds: number) => {
  if (!rows.length) return -1;

  const activeIndex = findActiveRowIndex(rows, seconds);
  if (activeIndex >= 0) return activeIndex;

  // First row starting at or after `seconds`; `rows.length` once the playhead is past them all.
  const nextIndex = countStartedBefore(rows.length, (index) => rows[index].startTimeSeconds, seconds);
  if (nextIndex === 0) return 0;
  if (nextIndex === rows.length) return rows.length - 1;

  // In a gap, so ties go to the row about to be spoken.
  const gapBefore = seconds - rows[nextIndex - 1].endTimeSeconds;
  const gapAfter = rows[nextIndex].startTimeSeconds - seconds;
  return gapBefore < gapAfter ? nextIndex - 1 : nextIndex;
};

/**
 * Highlight ids per row, positionally aligned to `rows`, so a fact styles every row its
 * time range spans and the row component indexes in without a per-tick map lookup.
 *
 * Both inputs are start-sorted — `processFactHighlights` in `new/lib/doc-viewer-utils.ts`
 * sorts `timeRanges` on the way out, and that ordering is load-bearing here — so this
 * sweeps with a pointer and an active set rather than filtering the highlights per row.
 */
export const mapHighlightsToRows = (rows: TranscriptRow[], timeHighlights: TimeRangeWithId[]) => {
  const highlightIdsByRow: (string[] | null)[] = new Array(rows.length).fill(null);
  const active: TimeRangeWithId[] = [];
  let nextHighlight = 0;

  rows.forEach((row, index) => {
    while (nextHighlight < timeHighlights.length && timeHighlights[nextHighlight].startSeconds < row.endTimeSeconds) {
      active.push(timeHighlights[nextHighlight]);
      nextHighlight += 1;
    }

    // Ranges that ended before this row can never match a later one either.
    for (let i = active.length - 1; i >= 0; i -= 1) {
      if (active[i].startSeconds + active[i].durationSeconds <= row.startTimeSeconds) active.splice(i, 1);
    }

    // The start check is redundant while rows are start-sorted with non-overlapping ends,
    // but keeping it makes the result identical to a per-row filter either way.
    const overlapping = active.filter((highlight) => highlight.startSeconds < row.endTimeSeconds);
    if (!overlapping.length) return;

    // One fact can contribute several ranges to a single row.
    highlightIdsByRow[index] = [...new Set(overlapping.map((highlight) => highlight.id))];
  });

  return highlightIdsByRow;
};
